/**
 * @fileoverview Centralized repository for all permission strings used in the application.
 * Using this constant object prevents typos and ensures consistency when checking permissions.
 * The strings must match the 'name' field from the backend's permissions API.
 */
export const PERMISSIONS = {
  // --- Dashboard ---
  DASHBOARD_READ: "read-dashboard-overview",

  // --- User Management ---
  USERS_READ: "read-users",
  USERS_UPDATE: "update-users", // For actions like suspend, activate
  USERS_DELETE: "delete-users",
  USER_ACTIVITY_READ: "read-user-activity",

  // --- Business Management ---
  BUSINESSES_READ: "read-businesses",
  BUSINESSES_CREATE: "create-businesses",
  BUSINESSES_UPDATE: "update-businesses",
  BUSINESSES_DELETE: "delete-businesses",

  // --- Account Management ---
  ACCOUNTS_READ: "read-accounts",
  ACCOUNTS_UPDATE: "update-accounts", // For settings, status changes
  ACCOUNTS_SUSPEND: "update-accounts", // Mapping suspend to a general update permission
  ACCOUNTS_CLOSE: "delete-accounts", // Mapping close to a delete permission

  // --- KYC / KYB ---
  KYC_READ: "read-kyc",
  KYC_MANAGE: "manage-kyc", // For approve/reject actions

  // --- Products ---
  PRODUCTS_READ: "read-products",
  PRODUCTS_UPDATE: "update-products", // For updating settings
  PRODUCTS_ANALYTICS_READ: "read-products-analytics",
  BANKING_PROVIDER_SETTINGS_READ: "read-banking-provider-settings",
  BANKING_PROVIDER_SETTINGS_UPDATE: "update-banking-provider-settings",

  // --- Transactions ---
  TRANSACTIONS_READ: "read-transactions",
  TRANSACTIONS_CREATE: "create-transactions", // For admin-initiated deposits/payments
  TRANSACTIONS_UPDATE: "update-transactions",
  TRANSACTIONS_REFUND: "process-refunds",
  TRANSACTIONS_HISTORY_READ: "read-transactions-history",

  // --- Loans ---
  LOANS_READ: "read-loans",
  LOANS_CREATE: "create-loans",
  LOANS_UPDATE: "update-loans",
  LOANS_DELETE: "delete-loans",

  // --- Support ---
  SUPPORT_TICKETS_READ: "read-support-tickets",
  SUPPORT_TICKETS_CREATE: "create-support-tickets",
  SUPPORT_TICKETS_UPDATE: "update-support-tickets",
  SUPPORT_TICKETS_DELETE: "delete-support-tickets",
  SUPPORT_TICKETS_RESOLVE: "resolve-tickets",

  // --- Templates ---
  CONTENT_READ: "read-content",
  CONTENT_CREATE: "create-content",
  CONTENT_UPDATE: "update-content",
  CONTENT_DELETE: "delete-content",

  // --- FAQS ---
  FAQS_READ: "read-faqs",
  FAQS_CREATE: "create-faqs",
  FAQS_UPDATE: "update-faqs",
  FAQS_DELETE: "delete-faqs",

  // --- Notifications ---
  NOTIFICATIONS_READ: "read-notifications",
  NOTIFICATIONS_CREATE: "create-notifications",
  NOTIFICATIONS_UPDATE: "update-notifications",
  NOTIFICATIONS_DELETE: "delete-notifications",

  // --- Agents ---
  AGENTS_READ: "read-agents",
  AGENTS_CREATE: "create-agents",
  AGENTS_UPDATE: "update-agents",
  AGENTS_DELETE: "delete-agents",

  // --- Security & Team Management ---
  ADMINS_READ: "read-admins",
  ADMINS_CREATE: "create-admins", // For inviting new members
  ADMINS_UPDATE: "update-admins",
  ADMINS_DELETE: "delete-admins",
  ROLES_READ: "read-roles",
  ROLES_CREATE: "create-roles",
  ROLES_UPDATE: "update-roles",
  INVITATIONS_CREATE: "create-invitations",
  INVITATIONS_READ: "read-invitations",
  INVITATIONS_UPDATE: "update-invitations",
  INVITATIONS_DELETE: "delete-invitations",
  SECURITY_SETTINGS_READ: "read-security-settings",
  SECURITY_SETTINGS_UPDATE: "update-security-settings",

  // --- Analytics & Reporting ---
  REPORTS_READ: "read-reports",
  FINANCIAL_REPORTS_READ: "read-financial-reports",
  COMPLIANCE_REPORTS_READ: "read-compliance-reports",
  DEEP_ANALYTICS_READ: "read-deep-analytics",

  // --- Audit ---
  AUDIT_LOGS_READ: "read-audit-logs",

  // --- AI / Insights ---
  AI_SETTINGS_READ: "read-ai-settings",
  AI_SETTINGS_UPDATE: "update-ai-settings",

  // --- Approvals (Maker-Checker) ---
  APPROVALS_READ: "read-approval-workflow",
  APPROVALS_MANAGE: "update-approval-workflow",
  APPROVALS_RULES_CREATE: "create-maker-checker-rules",
  APPROVALS_RULES_READ: "read-maker-checker-rules",
  APPROVALS_RULES_UPDATE: "update-maker-checker-rules",
  APPROVALS_RULES_DELETE: "delete-maker-checker-rules",
};
